import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface MemberManagementProps {
  domain: string;
}

interface Member {
  id: string;
  memberId: string;
  name: string;
  type: 'Student' | 'Teacher' | 'Staff';
  className: string;
  section: string;
  phone: string;
  email: string;
  maxBooks: number;
  booksIssued: number;
  status: 'active' | 'inactive';
  joinedOn: string;
}

const MemberManagement: React.FC<MemberManagementProps> = ({ domain }) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);

  const emptyForm = {
    memberId: '',
    name: '',
    type: 'Student' as 'Student' | 'Teacher' | 'Staff',
    className: '',
    section: '',
    phone: '',
    email: '',
    maxBooks: 3,
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (!domain) return;

    const membersRef = collection(db, `school-data/${domain}/library-members`);
    const q = query(membersRef, orderBy('name', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list: Member[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        list.push({
          id: docSnap.id,
          memberId: data.memberId || '',
          name: data.name || '',
          type: data.type || 'Student',
          className: data.className || '',
          section: data.section || '',
          phone: data.phone || '',
          email: data.email || '',
          maxBooks: data.maxBooks || 3,
          booksIssued: data.booksIssued || 0,
          status: data.status || 'active',
          joinedOn: data.joinedOn || '',
        });
      });
      setMembers(list);
      setLoading(false);
    }, (error) => {
      console.error('Error loading members:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [domain]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: name === 'maxBooks' ? parseInt(value) || 0 : value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.memberId || !formData.type) {
      alert('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (editingMember) {
        const memberRef = doc(db, `school-data/${domain}/library-members`, editingMember.id);
        await updateDoc(memberRef, {
          ...formData,
          updatedOn: new Date().toISOString(),
        });
      } else {
        await addDoc(collection(db, `school-data/${domain}/library-members`), {
          ...formData,
          booksIssued: 0,
          status: 'active',
          joinedOn: new Date().toISOString().split('T')[0],
        });
      }
      closeModal();
    } catch (error: any) {
      console.error('Error saving member:', error);
      alert(`Failed to save member: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (member: Member) => {
    if (member.booksIssued > 0) {
      alert('Cannot delete member with books issued. Return all books first.');
      return;
    }
    if (!confirm(`Remove "${member.name}" from library members?`)) return;
    try {
      await deleteDoc(doc(db, `school-data/${domain}/library-members`, member.id));
    } catch (error: any) {
      alert(`Failed to delete: ${error.message}`);
    }
  };

  const toggleStatus = async (member: Member) => {
    try {
      const memberRef = doc(db, `school-data/${domain}/library-members`, member.id);
      await updateDoc(memberRef, {
        status: member.status === 'active' ? 'inactive' : 'active',
      });
    } catch (error: any) {
      alert(`Failed to update status: ${error.message}`);
    }
  };

  const openEdit = (member: Member) => {
    setEditingMember(member);
    setFormData({
      memberId: member.memberId,
      name: member.name,
      type: member.type,
      className: member.className,
      section: member.section,
      phone: member.phone,
      email: member.email,
      maxBooks: member.maxBooks,
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingMember(null);
    setFormData(emptyForm);
  };

  const importStudents = async () => {
    setImporting(true);
    try {
      const studentsRef = collection(db, `school-users/${domain}/students`);
      const snapshot = await getDocs(studentsRef);

      const existingIds = new Set(members.map((m) => m.memberId));
      let imported = 0;

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const studentId = data.customUserId || data.enrollmentId || docSnap.id;
        if (existingIds.has(studentId)) continue;

        await addDoc(collection(db, `school-data/${domain}/library-members`), {
          memberId: studentId,
          name: data.name || data.studentName || '',
          type: 'Student',
          className: data.class || data.className || '',
          section: data.section || '',
          phone: data.phone || data.parentPhone || '',
          email: data.email || '',
          maxBooks: 3,
          booksIssued: 0,
          status: 'active',
          joinedOn: new Date().toISOString().split('T')[0],
        });
        imported++;
      }

      alert(`Imported ${imported} students as library members.`);
      setShowImportModal(false);
    } catch (error: any) {
      console.error('Error importing students:', error);
      alert(`Import failed: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const importTeachers = async () => {
    setImporting(true);
    try {
      const teachersRef = collection(db, `school-users/${domain}/teachers`);
      const snapshot = await getDocs(teachersRef);

      const existingIds = new Set(members.map((m) => m.memberId));
      let imported = 0;

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const teacherId = data.customUserId || data.employeeId || docSnap.id;
        if (existingIds.has(teacherId)) continue;

        await addDoc(collection(db, `school-data/${domain}/library-members`), {
          memberId: teacherId,
          name: data.name || data.teacherName || '',
          type: 'Teacher',
          className: '',
          section: '',
          phone: data.phone || '',
          email: data.email || '',
          maxBooks: 5,
          booksIssued: 0,
          status: 'active',
          joinedOn: new Date().toISOString().split('T')[0],
        });
        imported++;
      }

      alert(`Imported ${imported} teachers as library members.`);
      setShowImportModal(false);
    } catch (error: any) {
      console.error('Error importing teachers:', error);
      alert(`Import failed: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const filtered = members.filter((m) => {
    const matchesSearch = !search ||
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.memberId.toLowerCase().includes(search.toLowerCase()) ||
      m.email.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || m.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const activeCount = members.filter((m) => m.status === 'active').length;
  const studentCount = members.filter((m) => m.type === 'Student').length;
  const teacherCount = members.filter((m) => m.type === 'Teacher').length;

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2 text-muted">Loading members...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-people fs-3 text-primary"></i>
              <h4 className="mb-0 mt-1">{members.length}</h4>
              <small className="text-muted">Total Members</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-check-circle fs-3 text-success"></i>
              <h4 className="mb-0 mt-1">{activeCount}</h4>
              <small className="text-muted">Active</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-mortarboard fs-3 text-info"></i>
              <h4 className="mb-0 mt-1">{studentCount}</h4>
              <small className="text-muted">Students</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-person-workspace fs-3 text-warning"></i>
              <h4 className="mb-0 mt-1">{teacherCount}</h4>
              <small className="text-muted">Teachers</small>
            </div>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">
            <i className="bi bi-people me-2 text-primary-custom"></i>
            Library Members
          </h5>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary btn-sm" onClick={() => setShowImportModal(true)}>
              <i className="bi bi-download me-2"></i>Import
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)}>
              <i className="bi bi-plus-circle me-2"></i>Add Member
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, member ID, or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select className="form-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="all">All Types</option>
                <option value="Student">Students</option>
                <option value="Teacher">Teachers</option>
                <option value="Staff">Staff</option>
              </select>
            </div>
            <div className="col-md-3 text-end">
              <span className="text-muted small">{filtered.length} members</span>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 d-block mb-2"></i>
              {members.length === 0 ? 'No members yet. Add members or import from students/teachers.' : 'No members match your search.'}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Member ID</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Class</th>
                    <th>Contact</th>
                    <th className="text-center">Books Issued</th>
                    <th className="text-center">Max Books</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((member) => (
                    <tr key={member.id}>
                      <td><strong>{member.memberId}</strong></td>
                      <td>{member.name}</td>
                      <td>
                        <span className={`badge ${
                          member.type === 'Student' ? 'bg-info' :
                          member.type === 'Teacher' ? 'bg-primary' : 'bg-secondary'
                        }`}>
                          {member.type}
                        </span>
                      </td>
                      <td>
                        {member.className ? `${member.className}${member.section ? `-${member.section}` : ''}` : '-'}
                      </td>
                      <td>
                        <small>{member.phone || member.email || '-'}</small>
                      </td>
                      <td className="text-center">
                        <span className={`badge ${member.booksIssued > 0 ? 'bg-warning text-dark' : 'bg-light text-dark'}`}>
                          {member.booksIssued}
                        </span>
                      </td>
                      <td className="text-center">{member.maxBooks}</td>
                      <td>
                        <span className={`badge ${member.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                          {member.status}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => openEdit(member)} title="Edit">
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-warning me-1" onClick={() => toggleStatus(member)} title={member.status === 'active' ? 'Deactivate' : 'Activate'}>
                          <i className={`bi ${member.status === 'active' ? 'bi-pause-circle' : 'bi-play-circle'}`}></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(member)} title="Delete">
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-person-plus me-2"></i>
                  {editingMember ? 'Edit Member' : 'Add New Member'}
                </h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Member ID *</label>
                      <input type="text" className="form-control" name="memberId" value={formData.memberId} onChange={handleChange} required placeholder="e.g., STU001 or TCH001" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Member Type *</label>
                      <select className="form-select" name="type" value={formData.type} onChange={handleChange} required>
                        <option value="Student">Student</option>
                        <option value="Teacher">Teacher</option>
                        <option value="Staff">Staff</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Name *</label>
                      <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} required />
                    </div>
                    {formData.type === 'Student' && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label">Class</label>
                          <input type="text" className="form-control" name="className" value={formData.className} onChange={handleChange} placeholder="e.g., 10" />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Section</label>
                          <input type="text" className="form-control" name="section" value={formData.section} onChange={handleChange} placeholder="e.g., A" />
                        </div>
                      </>
                    )}
                    <div className="col-md-6">
                      <label className="form-label">Phone</label>
                      <input type="tel" className="form-control" name="phone" value={formData.phone} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input type="email" className="form-control" name="email" value={formData.email} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Max Books Allowed</label>
                      <input type="number" className="form-control" name="maxBooks" value={formData.maxBooks} onChange={handleChange} min={1} max={20} />
                    </div>
                  </div>
                  <div className="mt-4 d-flex gap-2">
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? (
                        <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
                      ) : (
                        <><i className="bi bi-check-circle me-2"></i>{editingMember ? 'Update' : 'Add Member'}</>
                      )}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-download me-2"></i>Import Members
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowImportModal(false)}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted">Import existing students and teachers as library members. Already registered members will be skipped.</p>
                <div className="d-grid gap-3">
                  <button className="btn btn-outline-info" onClick={importStudents} disabled={importing}>
                    {importing ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>Importing...</>
                    ) : (
                      <><i className="bi bi-mortarboard me-2"></i>Import Students</>
                    )}
                  </button>
                  <button className="btn btn-outline-primary" onClick={importTeachers} disabled={importing}>
                    {importing ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>Importing...</>
                    ) : (
                      <><i className="bi bi-person-workspace me-2"></i>Import Teachers</>
                    )}
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowImportModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberManagement;
